import type { ReactNode } from "react";

const REQUIRED_COLUMNS = ["Supplier", "Supplier Tradename", "RS Designation"];
const OPTIONAL_COLUMNS = [
  "Supplier Code",
  "Tradename Code",
  "Processing Application",
  "Standard Applied",
  "Reads As",
  "Base",
  "Hardness",
  "Fillers",
  "Additives",
  "Color",
  "Recycle",
  "Suffix",
  "Source",
];
const TDS_MATCHING_STEPS = [
  "The file name is compared with every saved grade.",
  "The first 3 pages of the PDF confirm the match, or find the grade when the name doesn't.",
  "You check the list, then nothing is attached until you click Attach.",
];
const TDS_FILE_NAME_ADVICE = [
  { label: "Best", hint: "The tradename exactly as in the nomenclature", fileNames: ["Marlex 9005.pdf"], isAvoided: false },
  {
    label: "Same tradename at two suppliers",
    hint: "Put the supplier first",
    fileNames: ["Chevron Marlex 9005.pdf"],
    isAvoided: false,
  },
  { label: "Avoid", hint: "Partial names", fileNames: ["9005.pdf"], isAvoided: true },
];

function GuidePanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <aside
      className="flex flex-col gap-3.25 min-w-0 rounded-lg border border-rs-jet bg-rs-black px-4 py-3.5 text-[13px]"
      aria-label={title}
    >
      <h3 className="m-0 text-[11.5px] font-semibold uppercase tracking-[0.07em] text-[#a9b0a1]">{title}</h3>
      {children}
    </aside>
  );
}

function GuideGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <b className="block mb-1.25 text-[12.5px] font-semibold text-white">{title}</b>
      {children}
    </div>
  );
}

function CodeSample({
  isStoredForm = false,
  isAvoided = false,
  children,
}: {
  isStoredForm?: boolean;
  isAvoided?: boolean;
  children: ReactNode;
}) {
  const textColor = isAvoided ? "text-rs-red" : isStoredForm ? "text-rs-green" : "text-rs-off-white";
  return (
    <code
      className={`rounded-[3px] px-1.5 py-0.5 font-mono text-[12.5px] ${isAvoided ? "bg-rs-red/12" : "bg-rs-jet"} ${textColor}`}
    >
      {children}
    </code>
  );
}

export function NomenclatureFileGuide() {
  return (
    <GuidePanel title="What the file needs">
      <GuideGroup title="Required columns">
        <ul className="m-0 p-0 list-none flex flex-col gap-1 text-rs-off-white">
          {REQUIRED_COLUMNS.map((columnName) => (
            <li key={columnName} className="flex gap-2">
              <span className="font-bold text-rs-green" aria-hidden="true">
                ✓
              </span>
              {columnName}
            </li>
          ))}
        </ul>
      </GuideGroup>
      <GuideGroup title="Optional columns">
        <span className="leading-normal text-[#a9b0a1]">{OPTIONAL_COLUMNS.join(", ")}</span>
      </GuideGroup>
      <GuideGroup title="Designation format">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[#a9b0a1]">
          <CodeSample>RS-PE-HD-NC</CodeSample>
          <span>and</span>
          <CodeSample>RS-[PE-HD]-NC</CodeSample>
          <span>are saved as</span>
          <CodeSample isStoredForm>RS-[PE-HD]-NC</CodeSample>
        </div>
      </GuideGroup>
    </GuidePanel>
  );
}

export function TdsMatchingGuide() {
  return (
    <GuidePanel title="How files are matched">
      <ol className="m-0 p-0 list-none flex flex-col gap-1 text-rs-off-white">
        {TDS_MATCHING_STEPS.map((step, stepIndex) => (
          <li key={step} className="flex gap-2">
            <span
              className="grid place-items-center size-4.5 flex-none rounded-full bg-rs-green text-[11px] text-rs-black"
              aria-hidden="true"
            >
              {stepIndex + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
      <GuideGroup title="File names">
        <div className="flex flex-col gap-2">
          {TDS_FILE_NAME_ADVICE.map((advice) => (
            <div key={advice.label}>
              <span className="block mb-1 text-xs text-[#a9b0a1]">
                <span className="text-rs-off-white">{advice.label}</span> · {advice.hint}
              </span>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                {advice.fileNames.map((fileName) => (
                  <CodeSample key={fileName} isAvoided={advice.isAvoided}>
                    {fileName}
                  </CodeSample>
                ))}
              </div>
            </div>
          ))}
        </div>
      </GuideGroup>
    </GuidePanel>
  );
}
